import { Component, h, Prop, Watch, State, Element, Host } from '@stencil/core';
import {
  DATA_VISUALIZATION_HUE,
  DATA_VISUALIZATION_HUE_VARIANT
} from '../../constants';
import { getCssColorCode } from '../../utils/color-helper';

@Component({
  tag: 'uui-percentile-bar',
  styleUrl: 'uui-percentile-bar.scss',
  shadow: true
})
export class UUIPercentileBar {
  @Element() element: HTMLElement;
  /** 
    the percentage score - should be between 0 and 100
  */
  @Prop() score: number = 0;
  /** 
    label to display 
  */
  @Prop() scorelabel?: string;
  /** 
    option to override the default colours
  */
  @Prop({ mutable: true }) colors?: string[] | string;
  /** 
    specifies which side of the bar the label should be rendered
  */
  @Prop() labelPosition?: 'left' | 'right' = 'left';
  /** 
    set the background color of the bar
  */
  @Prop() defaultBackgroundColor: string = 'var(--uui-gray-06)';
  /** 
    option to hide the score label
  */
  @Prop() isShowScoreLabel: boolean = true;

  hasInvalidDataSlot: boolean;

  public defaultColors = [
    DATA_VISUALIZATION_HUE_VARIANT.RED,
    DATA_VISUALIZATION_HUE_VARIANT.ORANGE,
    DATA_VISUALIZATION_HUE_VARIANT.YELLOW,
    DATA_VISUALIZATION_HUE_VARIANT.GREEN
  ];

  @State() scoreColor: string = this.getPercentileColor();

  @Watch('score')
  onScoreChanged(): void {
    this.scoreColor = this.getPercentileColor();
  }

  componentWillLoad(): void {
    this.hasInvalidDataSlot = !!this.element.querySelector(
      '[slot="invalid-data"]'
    );
  }

  getColor(index: number): string {
    let percentileColors = this.colors;
    if (typeof this.colors === 'string') {
      percentileColors = JSON.parse(this.colors);
    }

    if (percentileColors && percentileColors.length > index) {
      return getCssColorCode(percentileColors[index]);
    }

    return DATA_VISUALIZATION_HUE[this.defaultColors[index]];
  }

  getPercentileColor(): string {
    const segments = Array.from({ length: 4 }, (_, i) => (i + 1) * 25);
    const colorIndex = segments.findIndex(f => f >= this.score);

    return colorIndex == -1
      ? this.defaultBackgroundColor
      : this.getColor(colorIndex);
  }

  render() {
    const filledSegments = Math.floor(this.score / 25);
    const partialSegmentFillPercent = ((this.score % 25) / 25) * 100;
    const displayValue = this.scorelabel || this.score;

    return (
      <Host>
        {(isNaN(this.score) || +this.score < 0) && (
          <div class="uui-percentile-bar-container">
            {!this.hasInvalidDataSlot ? (
              <div>Score Not Available</div>
            ) : (
              <slot name="invalid-data" />
            )}
          </div>
        )}
        {!isNaN(this.score) && +this.score >= 0 && (
          <div class="uui-percentile-bar-container">
            {this.isShowScoreLabel && this.labelPosition === 'left' && (
              <div class="uui-percentile-bar-score">{displayValue}</div>
            )}
            <div class="uui-percentile-bar-segments">
              {Array.from({ length: 4 }).map((_, index) => (
                <div class="uui-percentile-bar-segment">
                  {index < filledSegments && (
                    <div
                      class="uui-percentile-bar-fill"
                      style={{ backgroundColor: this.scoreColor }}
                    ></div>
                  )}
                  {index == filledSegments && (
                    <div
                      class="uui-percentile-bar-fill"
                      style={{
                        background: `linear-gradient(to right, ${this.scoreColor} ${partialSegmentFillPercent}%, ${this.defaultBackgroundColor} 0%)`
                      }}
                    ></div>
                  )}
                </div>
              ))}
            </div>
            {this.isShowScoreLabel && this.labelPosition === 'right' && (
              <div class="uui-percentile-bar-score uui-percentile-bar-score-right">{displayValue}</div>
            )}
          </div>
        )}
      </Host>
    );
  }
}
